if (!customElements.get('wlsn-cart-drawer')) {
  class WLSNCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.isLoading = false;
      this.hasHydrated = false;
      this.hydrationPromise = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.cartState = null;
      this.giftManuallyRemoved = false;
      this.classes = {
        emptyCartBtn: 'js-wlsn-cart-drawer-empty-cart-btn',
      };

      this.elements = {
        emptyCartBtn: this.querySelector(`.${this.classes.emptyCartBtn}`),
      };

      this.giftSettings = {
        enabled: this.dataset.purchaseGiftEnabled === 'true',
        variantId: Number(this.dataset.giftProductVariantId),
        discountCode: this.dataset.giftDiscountCode?.trim(),
        threshold: Number(this.dataset.purchaseAmountThreshold) * 100
      };
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', (e) => {
        const container = e.target.closest('.wlsn-cart-drawer__item-crossell');
        if (!container) return;

        const select = e.target.closest('.js-crossell-option');
        if (!select) return;

        const index = parseInt(select.dataset.optionIndex, 10);
        const value = select.value;

        if (!container._state) {
          container._state = {
            selectedOptions: {},
            selectedBundle: null
          };
        }

        container._state.selectedOptions[index] = value;

        this.updateCrossSellVariant(container);
      });
      document.addEventListener('keydown', this.handleKeydown);

      this.ensureHydrated();
    }

    async ensureHydrated() {
      if (this.hasHydrated) return;
      if (this.hydrationPromise) {
        await this.hydrationPromise;
        return;
      }

      this.hydrationPromise = this.refreshDrawer()
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.hasHydrated = true;
          this.hydrationPromise = null;
      });

      await this.hydrationPromise;
    }


    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
    }

    handleClick(e) {
      if (this.isLoading) return;

      const inc = e.target.closest('.js-cart-drawer-quantity-increase');
      const dec = e.target.closest('.js-cart-drawer-quantity-decrease');
      const remove = e.target.closest('.js-cart-drawer-remove');
      const closeBtn = e.target.closest('.js-cart-drawer-close');
      const overlay = e.target.closest('.drawer__overlay');
      const removeAllItems = e.target.closest('.js-wlsn-cart-drawer-remove-all-items');
      const emptyCartBtn = e.target.closest(`.${this.classes.emptyCartBtn}`);
      const crossellAddBtn = e.target.closest('.js-wlsn-cart-drawer-crossell-add');
      const crossellClose = e.target.closest('.js-wlsn-cart-drawer-crossell-close');

      if (inc) {
        const key = inc.dataset.key;
        const bundle = inc.dataset.bundle;
        if (bundle) this.updateBundle(bundle, 1);
        else if (key) this.updateItem(key, 1);
        return;
      }

      if (dec) {
        const key = dec.dataset.key;
        const bundle = dec.dataset.bundle;
        if (bundle) {
          this.updateBundle(bundle, -1);
        } else if (key) {
          const item = this.cartState?.items?.find(i => i.key === key);

          if (item?.quantity === 1) {
            this.removeItem(key);
          } else {
            this.updateItem(key, -1);
          }
        }
        return;
      }

      if (remove) {
        const key = remove.dataset.key;
        const bundle = remove.dataset.bundle;
        if (bundle) this.removeBundle(bundle);
        else if (key) this.removeItem(key);
        return;
      }

      if (closeBtn || overlay) {
        this.closeDrawer();
        return;
      }

      if (emptyCartBtn && this.hasAttribute('open')) {
        this.closeDrawer();
        return;
      }

      if (removeAllItems) {
        this.removeAllItems();
        return;
      }

      if (crossellAddBtn) {
        this.addCrossSellProduct(crossellAddBtn);
        return;
      }

      if (crossellClose) {
        const crossell = crossellClose.closest('.js-wlsn-cart-drawer-crossell');

        if (crossell) {
          crossell.remove();
        }

        return;
      }
    }

    handleKeydown(e) {
      if (e.key === 'Escape' && this.hasAttribute('open')) {
        this.closeDrawer();
      }
    }

    async updateItem(key, delta) {
      if (!key) return;
      this.setLoading(true);
      try {
        const item = this.cartState?.items?.find(i => i.key === key);
        if (!item) return;

        let newQty = item.quantity + delta;
        if (newQty < 1) newQty = 1;

        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: { [key]: newQty } })
        });

        const cart = await res.json();
        await this.refreshDrawer({ cart: cart });
      } catch (err) {
        console.error(err);
        this.setLoading(false);
      }
    }

    async removeItem(key) {
      if (!key) return;
    
      const item = this.cartState?.items?.find(i => i.key === key);
    
      if (item?.properties?._wlsn_purchase_gift === 'true') {
        this.giftManuallyRemoved = true;
      }
    
      this.setLoading(true);
    
      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: { [key]: 0 } })
        });
    
        const cart = await res.json();
    
        await this.refreshDrawer({
          cart,
          skipGiftCheck: true
        });
      } catch (err) {
        console.error(err);
        this.setLoading(false);
      }
    }

    async updateBundle(bundleId, delta) {
      if (!bundleId) return;
    
      this.setLoading(true);
    
      try {
        const bundleItems = this.cartState?.items.filter(
          item => item.properties?._bundle_id === bundleId
        ) ?? [];
    
        const mainItem = bundleItems.find(
          item => item.properties?._product_type === 'main'
        );
    
        if (!mainItem) return;
    
        const newQuantity = mainItem.quantity + delta;
    
        const updates = Object.fromEntries(
          bundleItems.map(item => [
            item.key,
            newQuantity <= 0 ? 0 : newQuantity
          ])
        );
    
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ updates })
        });
    
        const cart = await res.json();
    
        await this.refreshDrawer({ cart });
      } catch (err) {
        console.error(err);
        this.setLoading(false);
      }
    }

    async removeBundle(bundleId) {
      if (!bundleId) return;
      this.setLoading(true);
      try {
        const updates = Object.fromEntries(
          this.cartState?.items
            .filter(item => item.properties?._bundle_id === bundleId)
            .map(item => [item.key, 0])
        );

        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });

        const cart = await res.json();
        await this.refreshDrawer({ cart: cart });
      } catch (err) {
        console.error(err);
        this.setLoading(false);
      }
    }

    async refreshDrawer(opts = {}) {
      const wasOpen = this.hasAttribute('open');

      const providedCart = opts && opts.cart ? opts.cart : null;

      try {
        const cart = providedCart || await fetch('/cart.js', {
          credentials: 'same-origin',
          cache: 'no-store'
        }).then(r => r.json());

        this.updateBubble(cart.item_count);

        const res = await fetch(`/?sections=${this.sectionId}`, { credentials: 'same-origin' });
        const data = await res.json();

        const wrapper = document.createElement('div');
        wrapper.innerHTML = data[this.sectionId];

        const newDrawer = wrapper.querySelector('wlsn-cart-drawer');
        if (!newDrawer) return;

        this.innerHTML = newDrawer.innerHTML;

        this.updateGiftProgress(cart);

        if (wasOpen) this.setAttribute('open', '');
        this.cartState = cart;

        if (!opts.skipGiftCheck) {
          await this.handlePurchaseGift(cart);
        }

        const hasDiscountCodes = Array.isArray(cart.discount_codes)
          && cart.discount_codes.some(discount => !!discount?.code);

        if (cart.item_count === 0 && hasDiscountCodes) {
          try {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ discount: "" })
            });
          } catch (e) {
            console.error('Failed to clear discounts', e);
          }
        }

      } catch (err) {
        console.error(err);
      } finally {
        this.setLoading(false);
      }
    }

    async removeAllItems() {
      this.setLoading(true);

      try {
        await fetch('/cart/clear.js', {
          method: 'POST',
          credentials: 'same-origin'
        });

        await this.refreshDrawer();

      } catch (err) {
        console.error(err);
        this.setLoading(false);
      }
    }

    updateBubble(count) {
      const triggers = document.querySelectorAll('.js-wlsn-header-cart-drawer-trigger');

      triggers.forEach((trigger) => {
        let bubble = trigger.querySelector('.js-wlsn-header-cart-icon-bubble');

        if (count > 0) {
          if (!bubble) {
            const bubbleClass = trigger.classList.contains('wlsn-header-nav__cart-icon')
              ? 'wlsn-header-nav__cart-icon-bubble'
              : 'wlsn-header__cart-icon-bubble';

            bubble = document.createElement('span');
            bubble.className = `${bubbleClass} wlsn-text-eyebrow-small js-wlsn-header-cart-icon-bubble`;
            trigger.appendChild(bubble);
          }
          bubble.textContent = count;
        } else if (bubble) {
          bubble.remove();
        }
      });
    }

    closeDrawer() {
      this.removeAttribute('open');
      document.body.classList.remove('body-lock');
    }

    openDrawer() {
      this.setAttribute('open', '');
      document.body.classList.add('body-lock');
      this.ensureHydrated();
    }

    setLoading(state) {
      this.isLoading = state;
      this.classList.toggle('is-loading', state);
    }

    async addCrossSellProduct(btn) {
      const variantId = btn.dataset.variantId;
      const sellingPlanId = btn.dataset.sellingPlanId;

      if (!variantId) return;

      this.setLoading(true);

      try {
        const payload = {
          items: [
            {
              id: Number(variantId),
              quantity: 1
            }
          ]
        };

        if (sellingPlanId) {
          payload.items[0].selling_plan = Number(sellingPlanId);
        }

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (!res.ok) {
          throw new Error('Failed to add cross sell product');
        }

        await this.refreshDrawer();

      } catch (err) {
        console.error(err);
      } finally {
        this.setLoading(false);
      }
    }

    // Purchase gift

    getGiftItem(cart = this.cartState) {
      if (!cart?.items) return null;

      return cart.items.find(
        item => item.properties?._wlsn_purchase_gift === 'true'
      );
    }

    getGiftEligibleSubtotal(cart) {
      const giftItem = this.getGiftItem(cart);

      if (!giftItem) {
        return cart.items_subtotal_price;
      }

      return cart.items_subtotal_price - giftItem.line_price;
    }

    updateGiftProgress(cart) {
      const progress = this.querySelector('.js-wlsn-gift-progress');
    
      if (!progress) return;
    
      const text = progress.querySelector('.js-wlsn-gift-progress-text');
      if (!text) return;
    
      const { threshold } = this.giftSettings;
    
      if (!threshold) {
        progress.style.display = 'none';
        return;
      }
    
      const eligibleSubtotal = this.getGiftEligibleSubtotal(cart);
      const remaining = threshold - eligibleSubtotal;
    
      if (remaining <= 0) {
        progress.style.display = 'none';
        return;
      }
    
      const remainingAmount = (remaining / 100).toFixed(0);
    
      text.textContent = `Spend $${remainingAmount} more to unlock FREE Audien Shields!`;
    
      progress.style.display = '';
    }

    async handlePurchaseGift(cart) {
      const {
        enabled,
        variantId,
        discountCode,
        threshold
      } = this.giftSettings;
      
    
      if (!enabled || !variantId || !discountCode || !threshold) {
        return;
      }
    
      const giftItem = this.getGiftItem(cart);
      const eligibleSubtotal = this.getGiftEligibleSubtotal(cart);
      const qualifies = eligibleSubtotal >= threshold;
    
      const giftDiscount = Array.isArray(cart.discount_codes)
        ? cart.discount_codes.find(
            discount =>
              discount?.code?.toLowerCase() === discountCode.toLowerCase()
          )
        : null;
    
      const hasGiftDiscount = !!giftDiscount;
    

      if (qualifies && this.giftManuallyRemoved) {
        return;
      }
    
      if (qualifies && !giftItem) {
        await this.addPurchaseGift();
        return;
      }
    
      if (qualifies && giftItem && !hasGiftDiscount) {
        await this.applyGiftDiscount(cart);
        return;
      }
   
      if (!qualifies) {
        this.giftManuallyRemoved = false;
    
        if (giftItem) {
          await this.removePurchaseGift(giftItem);
          return;
        }
    
        if (hasGiftDiscount) {
          await this.removeGiftDiscount(cart);
          return;
        }
      }
    }

    async addPurchaseGift() {
      const { enabled, variantId } = this.giftSettings;

      if (!enabled || !variantId) return;

      this.setLoading(true);

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: {
                  _wlsn_purchase_gift: 'true'
                }
              }
            ]
          })
        });

        if (!res.ok) {
          throw new Error('Failed to add purchase gift');
        }

        const cart = await fetch('/cart.js', {
          credentials: 'same-origin',
          cache: 'no-store'
        }).then(r => r.json());

        await this.applyGiftDiscount(cart);

      } catch (err) {
        console.error('Failed to add purchase gift:', err);
        this.setLoading(false);
      }
    }

    async applyGiftDiscount(cart) {
      const { discountCode } = this.giftSettings;

      if (!discountCode) return;

      try {
        const existingDiscountCodes = Array.isArray(cart.discount_codes)
          ? cart.discount_codes
              .map(discount => discount?.code)
              .filter(Boolean)
          : [];

        const hasGiftDiscount = existingDiscountCodes.some(
          code => code.toLowerCase() === discountCode.toLowerCase()
        );

        if (hasGiftDiscount) {
          await this.refreshDrawer({
            cart,
            skipGiftCheck: true
          });

          return;
        }

        const discountCodes = [
          ...existingDiscountCodes,
          discountCode
        ];

        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            discount: discountCodes.join(',')
          })
        });

        if (!res.ok) {
          throw new Error('Failed to apply gift discount');
        }

        const updatedCart = await res.json();

        await this.refreshDrawer({
          cart: updatedCart,
          skipGiftCheck: true
        });

      } catch (err) {
        console.error('Failed to apply gift discount:', err);
        this.setLoading(false);
      }
    }

    async removePurchaseGift(giftItem) {
      if (!giftItem?.key) return;

      this.setLoading(true);

      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            updates: {
              [giftItem.key]: 0
            }
          })
        });

        if (!res.ok) {
          throw new Error('Failed to remove purchase gift');
        }

        const cart = await res.json();

        await this.removeGiftDiscount(cart);

      } catch (err) {
        console.error('Failed to remove purchase gift:', err);
        this.setLoading(false);
      }
    }

    async removeGiftDiscount(cart) {
      const { discountCode } = this.giftSettings;

      if (!discountCode) {
        await this.refreshDrawer({
          cart,
          skipGiftCheck: true
        });

        return;
      }

      const existingDiscountCodes = Array.isArray(cart.discount_codes)
        ? cart.discount_codes
            .map(discount => discount?.code)
            .filter(Boolean)
        : [];

      const remainingDiscountCodes = existingDiscountCodes.filter(
        code => code.toLowerCase() !== discountCode.toLowerCase()
      );

      // Gift discount isn't applied
      if (remainingDiscountCodes.length === existingDiscountCodes.length) {
        await this.refreshDrawer({
          cart,
          skipGiftCheck: true
        });

        return;
      }

      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            discount: remainingDiscountCodes.join(',')
          })
        });

        if (!res.ok) {
          throw new Error('Failed to remove gift discount');
        }

        const updatedCart = await res.json();

        await this.refreshDrawer({
          cart: updatedCart,
          skipGiftCheck: true
        });

      } catch (err) {
        console.error('Failed to remove gift discount:', err);
        this.setLoading(false);
      }
    }
  }

  customElements.define('wlsn-cart-drawer', WLSNCartDrawer);
}